
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App.tsx';
import { AppStep } from '../types.ts';
import { extractStructure } from '../services/gemini.ts';
import { Loader2, CheckCircle2 } from 'lucide-react';

const ExtractionPage: React.FC = () => {
  const ctx = useContext(AppContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const process = async () => {
      if (!ctx?.state.imageData) return;
      try {
        setLoading(true);
        const data = await extractStructure(ctx.state.imageData);
        ctx.dispatch({ type: 'SET_EXTRACTION', payload: data });
      } catch (err: any) {
        ctx?.dispatch({ type: 'SET_ERROR', payload: err.message });
      } finally {
        setLoading(false);
      }
    };
    process();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Loader2 className="w-16 h-16 text-indigo-500 animate-spin mb-6" />
        <h2 className="text-2xl font-bold mb-2">Engine 1: Structural Extraction</h2>
        <p className="text-slate-400 max-w-md">Scanning pixels, identifying primitives, and mapping spatial coordinates... This is where the magic starts.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
      <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl overflow-hidden h-[600px] flex flex-col">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-indigo-400">
          <CheckCircle2 size={18} /> Raw Extraction Data
        </h3>
        <div className="flex-1 bg-slate-950 rounded-xl p-4 font-mono text-sm overflow-auto custom-scrollbar">
          <pre className="text-emerald-500">{JSON.stringify(ctx?.state.extractionData, null, 2)}</pre>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800 shadow-xl">
          <h2 className="text-2xl font-bold mb-4">Structural Analysis Complete</h2>
          <p className="text-slate-400 mb-6">
            Engine 1 has successfully mapped <strong>{ctx?.state.extractionData?.length || 0}</strong> unique UI elements from your sketch. 
            Spatial mapping is locked with 98% precision.
          </p>
          <div className="space-y-4 mb-8">
            <div className="flex justify-between items-center p-3 rounded-lg bg-slate-950 border border-slate-800">
              <span className="text-slate-400">Elements Detected</span>
              <span className="font-mono text-indigo-400 font-bold">{ctx?.state.extractionData?.length}</span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-slate-950 border border-slate-800">
              <span className="text-slate-400">Coordinate Precision</span>
              <span className="font-mono text-indigo-400 font-bold">1:1000 Grid</span>
            </div>
          </div>
          <button
            onClick={() => ctx?.dispatch({ type: 'SET_STEP', payload: AppStep.DESIGN })}
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-bold text-lg transition-all shadow-xl shadow-indigo-500/20"
          >
            Deploy Engine 2: Design Intelligence
          </button>
        </div>
        
        <div className="bg-indigo-600/10 border border-indigo-500/30 p-6 rounded-3xl">
          <h4 className="text-indigo-400 font-bold mb-2">Expert Definition:</h4>
          <p className="text-slate-300 text-sm italic">
            "AI Engine Pipeline: It is a sequential processing architecture where multiple specialized AI agents work in a chain. The first agent handles Object Detection and Spatial Mapping..."
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExtractionPage;
